import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Box, Divider, Stack, useTheme } from "@mui/material";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import NetValueTrendContent from "./NetValueTrendContent";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { lightModeNamedColors } from "theme/infegyColor";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import { QueryField, QueryInfo } from "system/query/QueryInfo";
import KeywordOptionSelector from "components/widgetParts/KeywordOptionSelector";
import WidgetPresetOptions from "components/widgetParts/WidgetPresetOptions";
import ColorSelector from "components/uiElements/inputs/colorSelector/ColorSelector";
import { AnyColor } from "react-colorful/dist/types";
import { socialDatasetId } from "system/query/Query";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, getSingleWidgetResponseByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { IntervalSelector } from "components/queries/queryBuilder/dialogParts/IntervalSelector";

export type NetValueTrendSettingsStateType = {
  abbreviate: boolean;
  alignment: string;
  color?: string;
  customWidgetTitle: string;
  showDecimals: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
  viewAsNetCount: boolean;
  viewAsPortion: boolean;
  topColor: string;
  bottomColor: string;
  neutralColor: string;
}

export const defaultSettings: NetValueTrendSettingsStateType = {
  abbreviate: false,
  alignment: 'center',
  color: undefined,
  customWidgetTitle: '',
  showDecimals: false,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
  viewAsNetCount: false,
  viewAsPortion: false,
  topColor: lightModeNamedColors.positive,
  bottomColor: lightModeNamedColors.negative,
  neutralColor: lightModeNamedColors.mixed,
}

const defaultWidgetRequest = {
  name: 'net-value-trend',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
};

export function BuildDefaultNetValueTrendRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = { aggs: {} };

  if (queryInfo?.dataset?.id === socialDatasetId) {
    requestInitBody.aggs = { ["net_value_trend"]: {
      field: "published",
      op: "histogram",
      aggs: {
        net_value: {
          op: "keyword",
          field: "sentiment",
          filter: ["p", "n"]
        }
      }
    }};
  } else {
    const foundTimestampField = queryInfo.fields?.find((field: QueryField) => field.type === "timestamp");
    const foundField = queryInfo.fields?.find((field: QueryField) => {
      const numberOfOptions = parseInt(field?.options?.length + "");
      return ["keyword", "keywords"].includes(field.type) && numberOfOptions > 1;
    });
    if (foundTimestampField && foundField) {
      const firstOption = foundField?.options?.[0];
      const secondOption = foundField?.options?.[1];
      if (firstOption && firstOption.id && firstOption.label) {
        requestInitBody.aggs = { ["net_value_trend"]: {
          field: foundTimestampField.id,
          op: "histogram",
          aggs: {
            net_value: {
              op: "keyword",
              field: foundField.id,
              filter: [firstOption.id, secondOption.id]
            }
          }
        }};
      }
    }
  }

  if (Object.keys(requestInitBody.aggs).length) {
    widgetRequest.requestInit = { body: requestInitBody };
    return [widgetRequest];
  }
}

export default function NetValueTrendSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'net-value-trend');
  const requestBody = originalRequest?.requestInit?.body || {};

  const updatedDefaultSettings = {...defaultSettings, color: theme.colors.primary.dark, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<NetValueTrendSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: NetValueTrendSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const [timestampField, setTimestampField] = useState<string | undefined>();
  const [yAxisField, setYAxisField] = useState<string | undefined>();
  const [topOption, setTopOption] = useState<string | undefined>();
  const [bottomOption, setBottomOption] = useState<string | undefined>();
  const [topOptionLabel, setTopOptionLabel] = useState<string | undefined>(requestBody?.aggs?.net_value_trend?.aggs?.net_value?.labels?.[0]);
  const [bottomOptionLabel, setBottomOptionLabel] = useState<string | undefined>(requestBody?.aggs?.net_value_trend?.aggs?.net_value?.labels?.[1]);
  const [interval, setInterval] = useState<string>("__auto__");

  useEffect(() => {
    const aggs = requestBody?.aggs?.["net_value_trend"];
    // extract timestamp field from aggs
    let timestampField = aggs?.field;

    if (!timestampField) {
      const foundTimestampField = props.queryInfo?.fields?.find((field: QueryField) => field.type === "timestamp");
      if (foundTimestampField) {
        timestampField = foundTimestampField.id;
      }
    }

    // extract interval from aggs
    let interval = aggs?.interval;
    if (!interval) {
      interval = "__auto__";
    }

    // extract y-axis field from aggs
    let yAxisField = aggs?.aggs?.net_value?.field;
    if (!yAxisField) {
      const foundField = props.queryInfo?.fields?.find((field: QueryField) => {
        const numberOfOptions = parseInt(field?.options?.length + "");
        return ["keyword", "keywords"].includes(field.type) && numberOfOptions > 1;
      });
      if (foundField) {
        yAxisField = foundField.id;
      }
    }

    // extract top and bottom options from aggs
    let topOption = aggs?.aggs?.net_value?.filter?.[0];
    if (!topOption) {
      const firstOption = props.queryInfo?.fields?.find((field: QueryField) => field.id === yAxisField)?.options?.[0];
      if (firstOption) {
        topOption = firstOption.id;
      }
    }

    // extract bottom option from aggs
    let bottomOption = aggs?.aggs?.net_value?.filter?.[1];
    if (!bottomOption) {
      const secondOption = props.queryInfo?.fields?.find((field: QueryField) => field.id === yAxisField)?.options?.[1];
      if (secondOption) {
        bottomOption = secondOption.id;
      }
    }

    setTimestampField(timestampField);
    setYAxisField(yAxisField);
    setTopOption(topOption);
    setBottomOption(bottomOption);
    setInterval(interval);
  }, [requestBody]);

  const updateAggs = () => {
    if (!timestampField || !yAxisField || !topOption || !bottomOption) {
      return;
    }

    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };

    requestInitBody.aggs = {
        net_value_trend: {
          field: timestampField,
          op: "histogram",
          aggs: {
            net_value: {
              op: "keyword",
              field: yAxisField,
              filter: [topOption, bottomOption],
              labels: [topOptionLabel, bottomOptionLabel]
            }
          }
        }
      };

    if (interval && interval !== "__auto__") {
      requestInitBody.aggs.net_value_trend.interval = interval;
    }

    let requests:Array<WidgetRequest> = [];
    if (Object.keys(requestInitBody.aggs).length) {
      widgetRequest.requestInit = { body: requestInitBody };
      requests = [widgetRequest];
    }

    props.saveWidgetDataAndRequests({
      settings: settingsState
    }, requests).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  const sentimentField = props.queryInfo?.fields?.find((field: QueryField) => field.id === "sentiment");
  const genderField = props.queryInfo?.fields?.find((field: QueryField) => field.id === "gender");

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.net-value-trend.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            {/* Widget Preset Options - only available for social datasets */}
            {props.queryInfo?.dataset?.id === socialDatasetId &&
              <WidgetPresetOptions
                options={[{
                  label: i18n.t("widget-types.net-value.settings.preset-sentiment"),
                  value: "sentiment",
                },{
                  label: `${i18n.t("sentiment")}: ${i18n.t("positivity")}`,
                  value: "positivity",
                },{
                  label: i18n.t("widget-types.net-value.settings.preset-gender"),
                  value: "gender",
                }]}
                setSelectedOption={(newValue: string) => {
                  if (newValue === "sentiment") {
                    setTimestampField("published");
                    setYAxisField(sentimentField?.id);
                    setTopOption("p");
                    setBottomOption("n");
                    setSettingsState({
                      ...settingsState,
                      topColor: lightModeNamedColors.positive,
                      bottomColor: lightModeNamedColors.negative,
                      neutralColor: lightModeNamedColors.mixed,
                      viewAsNetCount: false,
                      viewAsPortion: false,
                    });
                  } else if (newValue === "positivity") {
                    setTimestampField("published");
                    setYAxisField(sentimentField?.id);
                    setTopOption("p");
                    setBottomOption("n");
                    setSettingsState({
                      ...settingsState,
                      topColor: lightModeNamedColors.positive,
                      bottomColor: lightModeNamedColors.negative,
                      neutralColor: lightModeNamedColors.mixed,
                      viewAsPortion: true,
                      viewAsNetCount: false,
                    });
                    setTabValue(1);
                  } else if (newValue === "gender") {
                    setTimestampField("published");
                    setYAxisField(genderField?.id);
                    setTopOption("f");
                    setBottomOption("m");
                    setSettingsState({
                      ...settingsState,
                      topColor: lightModeNamedColors.female,
                      bottomColor: lightModeNamedColors.male,
                      neutralColor: lightModeNamedColors.neutral,
                      viewAsNetCount: false,
                      viewAsPortion: false,
                    });
                  }
                }}>
              </WidgetPresetOptions>
            }
            <SimpleFormLabel>{i18n.t("field")}</SimpleFormLabel>
            <QueryFieldSelector
              allowedFieldTypes={["timestamp"]}
              fields={props.queryInfo?.fields}
              selectedFieldId={timestampField || ""}
              setSelectedFieldId={(newFieldId:string) => {
                setTimestampField(newFieldId);
              }}
            />
            <SimpleFormLabel>{i18n.t("interval")}</SimpleFormLabel>
            <IntervalSelector
              selectedInterval={interval}
              setSelectedInterval={(newInterval:string) => {
                setInterval(newInterval);
              }}/>
            <SimpleFormLabel>{i18n.t("field")}</SimpleFormLabel>
            <QueryFieldSelector
              allowedFieldTypes={["keyword", "keywords"]}
              fields={props.queryInfo?.fields}
              selectedFieldId={yAxisField || ""}
              setSelectedFieldId={(newFieldId:string) => {
                setYAxisField(newFieldId);
                setTopOption(undefined);
                setBottomOption(undefined);
                setTopOptionLabel(undefined);
                setBottomOptionLabel(undefined);
              }}
            />

            {/* Top Value */}
            <SimpleFormLabel>{i18n.t("widget-types.net-value.top-value")}</SimpleFormLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2 }}>
              <ColorSelector
                color={settingsState.topColor ?? ''}
                showInput={false}
                onChange={(color: AnyColor) => {
                  setSettingsState({
                    ...settingsState,
                    topColor: color.toString(),
                  });
                }} />
              <KeywordOptionSelector
                key={`top-value-selector-${yAxisField || ""}`}
                queryInfo={props.queryInfo}
                selectedField={props.queryInfo?.fields?.find((field: QueryField) => field.id === yAxisField)}
                enforceFixedSelection={true}
                selectedKeywordOption={topOption}
                selectedKeywordLabel={topOptionLabel}
                setSelectedKeywordOption={(newKeywordOption: string | undefined, newKeywordOptionLabel?: string | undefined) => {
                  if (!newKeywordOption) {
                    newKeywordOption = "";
                  }
                  setTopOption(newKeywordOption);
                  setTopOptionLabel(newKeywordOptionLabel);
              }} />
            </Box>

            {/* Bottom Value */}
            <SimpleFormLabel>{i18n.t("widget-types.net-value.bottom-value")}</SimpleFormLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2 }}>
              <ColorSelector
                color={settingsState.bottomColor ?? ''}
                showInput={false}
                onChange={(color: AnyColor) => {
                  setSettingsState({
                    ...settingsState,
                    bottomColor: color.toString(),
                  });
                }} />
              <KeywordOptionSelector
                key={`bottom-value-selector-${yAxisField || ""}`}
                queryInfo={props.queryInfo}
                selectedField={props.queryInfo?.fields?.find((field: QueryField) => field.id === yAxisField)}
                enforceFixedSelection={true}
                excludeOptions={topOption ? [topOption] : []}
                selectedKeywordOption={bottomOption}
                selectedKeywordLabel={bottomOptionLabel}
                setSelectedKeywordOption={(newKeywordOption: string | undefined, newKeywordOptionLabel?: string | undefined) => {
                  if (!newKeywordOption) {
                    newKeywordOption = "";
                  }
                  setBottomOption(newKeywordOption);
                  setBottomOptionLabel(newKeywordOptionLabel);
                }}
              />
            </Box>
          </>)}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <Box sx={{ position:"relative", height: "200px"}}>
              <NetValueTrendContent
                topValues={[100, 200, 300, 400, 500, 200, 100, 200, 300, 400, 500]}
                bottomValues={[300, 200, 100, 200, 300, 400, 500, 200, 100, 200, 300]}
                xFieldLabel={i18n.t("field")}
                xFieldValues={["2023-01-01", "2023-01-02", "2023-01-03", "2023-01-04", "2023-01-05", "2023-01-06", "2023-01-07", "2023-01-08", "2023-01-09", "2023-01-10", "2023-01-11"]}
                xFieldLabels={["1 - 01", "1 - 02", "1 - 03", "1 - 04", "1 - 05", "1 - 06", "1 - 07", "1 - 08", "1 - 09", "1 - 10", "1 - 11"]}
                topLabel={i18n.t("top")}
                bottomLabel={i18n.t("bottom")}
                settingsState={settingsState}
                yFieldLabel={i18n.t("field")} />
            </Box>

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                {/* Widget Title Alignment */}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}
